import type { ActionStep, Field, FunctionsKnown, OpenStep, Page, PanelSide, Tone } from '@fieldia/core';
import type { ElementFactory } from './chrome';
import type { DesignerWords } from './designer-words';
import { segmented } from './panel-controls';
import { formulaBox } from './rules-formula-box';
import { formulaProblem, literalOf } from './rules-formula';
import type { SavedFormRef } from './saved-forms';
import { shownTitle } from './saved-forms';
import { SETTABLE, type StepPatch } from './steps-commands';
import { stepMap, type MapChoice } from './steps-map';
import { showTargets } from './steps-places';

/**
 * A step's settings, opened in place under its sentence: for each kind only
 * what it takes — the page it opens and how (a panel, from which side), what that page starts with and
 * where its answers go; the field a value is set in and the value; what is
 * said or asked; the app's action — and, for every kind, "Only when…". The
 * page's fields are picked by their labels; formulas are typed in the rules'
 * formula box, the fields they read suggested.
 */

export interface StepContext {
  words: DesignerWords;
  /** The app's own functions a formula may call. */
  functions?: FunctionsKnown;
  page(): Page;
  /** Change the step: kept when it can be, else said why under it. With `typing`, a run in one box is one undo step. */
  change(patch: StepPatch, typing?: boolean): void;
  /** A saved page by its id, when the app's saved pages have it. */
  saved(id: string): Page | null | undefined;
  /** The app's saved pages to open; null without a store that lists them, or while they load. */
  listed(): SavedFormRef[] | null;
}

export interface StepSettings {
  element: HTMLElement;
  update(step: ActionStep): void;
  focusFirst(): void;
}

const focused = (node: Element) => node.ownerDocument.activeElement === node;

/**
 * What is typed in a step's Address as an address is typed — a scheme (`https:`, `mailto:`), or a path of this site
 * (`/help`) — with no space and no quotes of both kinds in it: its words; null where it is a formula.
 */
export function addressWords(typed: string): string | null {
  const words = typed.trim();
  if (!words || /\s/.test(words) || /^['"]/.test(words) || (words.includes("'") && words.includes('"'))) return null;
  return /^[a-z][a-z\d+.-]*:/i.test(words) || /^\/(?!\/)/.test(words) ? words : null;
}

/** An address's words as a formula holds them: in quotes it does not hold. */
const quotedWords = (words: string) => (words.includes("'") ? `"${words}"` : `'${words}'`);

/**
 * Whether a button may open an address that starts so, as the page's checks read it (@fieldia/core's references.ts): a
 * web, mail or phone address, or a path of this site — never a script, nor a page written as data.
 */
export function opensHere(words: string): boolean {
  const scheme = /^([a-z][a-z\d+.-]*):/i.exec(words.replace(/[\t\n\r]/g, '').replace(/^[\0- ]+/, ''))?.[1].toLowerCase();
  return !scheme || ['http', 'https', 'mailto', 'tel'].includes(scheme);
}
const ANOTHER = '#another';

/** A field's words for a choice of fields: its label, or its name. */
const choiceOf = ([name, def]: [string, Field]): MapChoice => ({ value: name, label: def.label || name });

function row(el: ElementFactory, label: string, ...controls: HTMLElement[]): HTMLElement {
  return el('div', { class: 'fd-answer-rule-field' }, el('span', { class: 'fd-answer-rule-word' }, label), ...controls);
}

function select(el: ElementFactory, label: string): HTMLSelectElement {
  return el('select', { class: 'fd-input fd-select', 'aria-label': label });
}

/** The options of a select, drawn again only when they change. */
function fill(el: ElementFactory, box: HTMLSelectElement, options: MapChoice[]) {
  const key = JSON.stringify(options);
  if (box.dataset['options'] === key) return;
  box.dataset['options'] = key;
  const was = box.value;
  box.replaceChildren(...options.map((o) => el('option', { value: o.value }, o.label)));
  box.value = was;
}

export function stepSettings(el: ElementFactory, kind: ActionStep['do'], ctx: StepContext): StepSettings {
  const words = ctx.words;
  const w = words.steps;
  const parts: HTMLElement[] = [];
  const updates: ((step: ActionStep) => void)[] = [];
  let first: HTMLElement | null = null;
  const firstIs = (control: HTMLElement) => (first = first ?? control);
  let current: ActionStep = { do: kind } as ActionStep;
  const fieldsOf = (test: (def: Field) => boolean = () => true) => Object.entries(ctx.page().fields).filter(([, def]) => test(def)).map(choiceOf);

  /** A field picked from this page's: the kinds it may be. */
  const fieldPicker = (label: string, key: 'field', test?: (def: Field) => boolean) => {
    const box = select(el, label);
    box.addEventListener('change', () => ctx.change({ [key]: box.value }));
    parts.push(row(el, label, box));
    firstIs(box);
    updates.push((step) => {
      fill(el, box, fieldsOf(test));
      if (!focused(box)) box.value = (step as { field?: string }).field ?? '';
    });
  };
  /** Words typed: what is said or asked, the app's action, a title. */
  const text = (label: string, key: 'message' | 'action' | 'title', placeholder: string, extra: Record<string, string> = {}) => {
    const box = el('input', { class: 'fd-input', 'aria-label': label, placeholder, autocomplete: 'off', ...extra });
    box.addEventListener('input', () => ctx.change({ [key]: box.value.trim() ? box.value : key === 'title' ? null : '' }, true));
    parts.push(row(el, label, box));
    firstIs(box);
    updates.push((step) => {
      if (!focused(box)) box.value = String((step as unknown as Record<string, unknown>)[key] ?? '');
    });
    return box;
  };
  /** A formula over this page's fields. */
  const formula = (label: string, key: 'value' | 'record' | 'url', placeholder: string, optional = false) => {
    const box = formulaBox(el, {
      words,
      label,
      placeholder,
      check: (page, source) => formulaProblem(page, source, words, ctx.functions),
      commit: (source) => ctx.change({ [key]: source || (optional ? null : '') }, true),
    });
    parts.push(row(el, label, box.element, box.problem));
    firstIs(box.input);
    updates.push((step) => box.update(ctx.page(), String((step as unknown as Record<string, unknown>)[key] ?? '')));
  };

  /**
   * The address a step opens, typed as an address is — `https://example.com/portal`, `/help`, `mailto:…`, `tel:…` — kept
   * as its words (the page stores them quoted), and shown back so; a formula where it is one (`'https://' + website`: a
   * quote, a field's name, a sign). An address no button may open (`javascript:`, a page written as data), words or a
   * formula's own first words, is said under the box in plain words, the typing kept and the page's address as it was
   * (0.35.1: an address typed plainly was refused as a formula, and a quoted script took its step away with it).
   */
  const address = () => {
    const box = formulaBox(el, {
      words,
      label: w.address,
      placeholder: w.addressPlaceholder,
      asWords: (typed) => addressWords(typed) !== null,
      check: (page, typed) => {
        const plain = addressWords(typed);
        const opens = plain ?? /^\s*(['"])([\s\S]*?)\1/.exec(typed)?.[2] ?? null;
        if (opens !== null && !opensHere(opens)) return { words: w.addressOnly, plain: true };
        return plain !== null ? null : formulaProblem(page, typed, words, ctx.functions);
      },
      commit: (typed) => {
        const plain = addressWords(typed);
        ctx.change({ url: plain !== null ? quotedWords(plain) : typed }, true);
      },
    });
    parts.push(row(el, w.address, box.element, box.problem));
    firstIs(box.input);
    updates.push((step) => {
      const url = step.do === 'openUrl' ? step.url : '';
      const words = literalOf(url)?.value;
      box.update(ctx.page(), typeof words === 'string' && addressWords(words) === words ? words : url);
    });
  };

  switch (kind) {
    case 'open':
      openSettings();
      break;
    case 'set':
      fieldPicker(w.field, 'field', (def) => SETTABLE.has(def.type));
      formula(w.setTo, 'value', w.setToPlaceholder);
      break;
    case 'clear':
      fieldPicker(w.field, 'field');
      break;
    case 'addLine':
      addLineSettings();
      break;
    case 'check':
      checkSettings();
      break;
    case 'goTo': {
      const box = select(el, w.goToTarget);
      box.addEventListener('change', () => ctx.change({ target: box.value }));
      parts.push(row(el, w.goToTarget, box));
      firstIs(box);
      updates.push((step) => {
        fill(el, box, showTargets(ctx.page()).map((t) => ({ value: t.id, label: t.label || t.id })));
        if (!focused(box) && step.do === 'goTo') box.value = step.target;
      });
      break;
    }
    case 'say': {
      text(w.words, 'message', w.sayPlaceholder);
      // Five tones do not sit side by side in a narrow panel: a list of them.
      const tone = select(el, w.tone);
      tone.append(...(['info', 'success', 'warning', 'danger', 'muted'] as const).map((value) => el('option', { value }, w.tones[value])));
      tone.addEventListener('change', () => ctx.change({ tone: tone.value === 'info' ? null : (tone.value as Tone) }));
      parts.push(row(el, w.tone, tone));
      updates.push((step) => {
        if (!focused(tone)) tone.value = step.do === 'say' ? (step.tone ?? 'info') : 'info';
      });
      break;
    }
    case 'ask':
      text(w.words, 'message', w.askPlaceholder);
      break;
    case 'call': {
      text(w.action, 'action', w.actionPlaceholder, { spellcheck: 'false', class: 'fd-input fd-answer-rule-code' });
      parts.push(el('p', { class: 'fd-properties-hint fd-set-hint' }, w.actionHint));
      break;
    }
    case 'openUrl': {
      address();
      // A new tab is the default, so only "no" is written.
      const newTab = el('input', { type: 'checkbox', class: 'fd-check' }) as HTMLInputElement;
      newTab.addEventListener('change', () => ctx.change({ newTab: newTab.checked ? null : false }));
      // Its box and its words apart, its focus ring clear of the first letter (the 0.36 walk's "n a new tab").
      parts.push(el('label', { class: 'fd-prop-check fd-inline-setting' }, newTab, w.inNewTab));
      updates.push((step) => {
        newTab.checked = step.do !== 'openUrl' || step.newTab !== false;
      });
      break;
    }
    case 'reload':
      parts.push(el('p', { class: 'fd-properties-hint fd-set-hint' }, w.reloadHint));
      break;
    case 'archive':
    case 'unarchive':
    case 'duplicate':
    case 'delete':
      parts.push(el('p', { class: 'fd-properties-hint fd-set-hint' }, w.recordHint));
      break;
    case 'print':
      parts.push(el('p', { class: 'fd-properties-hint fd-set-hint' }, w.printHint));
      break;
    case 'post': {
      text(w.words, 'message', w.postPlaceholder);
      parts.push(el('p', { class: 'fd-properties-hint fd-set-hint' }, w.postHint));
      // A note unless it goes to the followers: only "message" is written.
      const kind = select(el, w.postAs);
      kind.append(...(['note', 'message'] as const).map((value) => el('option', { value }, w.postKinds[value])));
      kind.addEventListener('change', () => ctx.change({ kind: kind.value === 'message' ? 'message' : null }));
      parts.push(row(el, w.postAs, kind));
      updates.push((step) => {
        if (!focused(kind)) kind.value = step.do === 'post' ? (step.kind ?? 'note') : 'note';
      });
      break;
    }
    default:
      break;
  }
  whenSetting();

  // ---- open a page ----
  function openSettings() {
    const page = select(el, w.page);
    const byId = el('input', { class: 'fd-input fd-answer-rule-code', 'aria-label': w.pageId, placeholder: w.pageIdPlaceholder, spellcheck: 'false', autocomplete: 'off' });
    let another = false;
    page.addEventListener('change', () => {
      another = page.value === ANOTHER;
      byId.hidden = !another;
      if (another) byId.focus();
      else if (page.value) ctx.change({ page: page.value });
    });
    byId.addEventListener('input', () => byId.value.trim() && ctx.change({ page: byId.value.trim() }, true));
    parts.push(row(el, w.page, page, byId));
    firstIs(page);
    // A side is a panel's alone: a dialog or its place drops it.
    const as = segmented<'dialog' | 'panel' | 'page'>(
      el,
      w.opensIn,
      (['dialog', 'panel', 'page'] as const).map((value) => ({ value, words: w.asShort[value], label: w.as[value] })),
      (value) => value && ctx.change(value === 'panel' ? { as: value } : { as: value === 'dialog' ? null : value, side: null })
    );
    parts.push(row(el, w.opensIn, as.element));
    // Where a panel comes from: five do not sit side by side in a narrow panel, so a list; the end of the line is unset.
    const from = select(el, w.from);
    from.addEventListener('change', () => ctx.change({ side: (from.value || null) as PanelSide | null }));
    const fromHint = el('p', { class: 'fd-properties-hint fd-set-hint' });
    const fromRow = row(el, w.from, from, fromHint);
    parts.push(fromRow);
    text(w.title, 'title', w.titlePlaceholder);
    formula(w.record, 'record', w.recordPlaceholder, true);
    const opened = () => (current.do === 'open' ? ctx.saved(current.page) : null);
    const startsWith = stepMap(el, {
      words,
      functions: ctx.functions,
      label: w.startsWith,
      addWords: w.addValue,
      nameLabel: w.itsField,
      names: () => {
        const other = opened();
        return other ? Object.entries(other.fields).map(choiceOf) : null;
      },
      valueLabel: (name) => w.valueFrom(name),
      reads: () => ctx.page(),
      commit: (map, typing) => ctx.change({ values: map }, typing),
    });
    // What comes back is read over the opened page's answers, and the record it saved.
    const answers = (): Page => {
      const other = opened();
      return { ...ctx.page(), fields: { ...(other?.fields ?? {}), id: { type: 'integer', label: w.theRecordItSaved } } };
    };
    const into = stepMap(el, {
      words,
      functions: ctx.functions,
      label: w.answersGo,
      addWords: w.addAnswer,
      nameLabel: w.thisFormsField,
      names: () => fieldsOf(),
      values: () => {
        const other = opened();
        return other ? [...Object.entries(other.fields).map(choiceOf), { value: 'id', label: w.theRecordItSaved }] : null;
      },
      valueLabel: (name) => w.valueFrom(name),
      reads: answers,
      commit: (map, typing) => ctx.change({ into: map }, typing),
    });
    // A list opened on some records only: its field against one of this form's, or its id. Further conditions are kept as written.
    const its = select(el, w.onlyRecords);
    const ours = select(el, w.isThisForms);
    const simpleFirst = (step: OpenStep) => {
      const first = step.filter?.[0];
      return first && 'op' in first && first.op === '=' && first.valueFrom !== undefined ? first : null;
    };
    const commitOnly = () => {
      if (current.do !== 'open') return;
      const rest = simpleFirst(current) ? (current.filter ?? []).slice(1) : (current.filter ?? []);
      const filter = its.value && ours.value ? [{ field: its.value, op: '=' as const, valueFrom: ours.value }, ...rest] : rest;
      ctx.change({ filter: filter.length ? filter : null });
    };
    its.addEventListener('change', commitOnly);
    ours.addEventListener('change', commitOnly);
    const onlyMore = el('p', { class: 'fd-properties-hint fd-set-hint' });
    const onlyRow = row(el, w.onlyRecords, its, el('span', { class: 'fd-prop-between' }, w.isThisForms), ours, onlyMore);
    parts.push(onlyRow, startsWith.element, into.element);
    updates.push((step) => {
      if (step.do !== 'open') return;
      const other = opened();
      onlyRow.hidden = !(other?.layout.type === 'list' || step.filter?.length);
      if (onlyRow.hidden) return;
      const first = simpleFirst(step);
      fill(el, its, [{ value: '', label: w.anyRecord }, ...Object.entries(other?.fields ?? {}).map(choiceOf)]);
      fill(el, ours, [{ value: 'id', label: w.theRecordItself }, ...fieldsOf()]);
      if (!focused(its)) its.value = first?.field ?? '';
      if (!focused(ours)) ours.value = first?.valueFrom ?? 'id';
      const more = (step.filter?.length ?? 0) - (first ? 1 : 0);
      onlyMore.textContent = more ? w.moreConditions(more).trim() : '';
      onlyMore.hidden = !more;
    });
    updates.push((step) => {
      if (step.do !== 'open') return;
      const listed = ctx.listed();
      const known = !!listed?.some((ref) => ref.id === step.page);
      const choices = [
        ...(!step.page ? [{ value: '', label: w.pick }] : []),
        ...(listed ?? []).map((ref) => ({ value: ref.id, label: shownTitle(ctx.saved(ref.id), ctx.page().language) || ref.title || ref.id })),
        ...(listed ? [{ value: ANOTHER, label: w.pageById }] : []),
      ];
      fill(el, page, choices);
      page.hidden = !listed;
      // A page the list has not got is typed by its id.
      another = another || (!!step.page && !known);
      if (!focused(page)) page.value = another || !listed ? ANOTHER : step.page;
      // The list come while its id box had the cursor: the list takes it.
      const typing = focused(byId);
      byId.hidden = !!listed && !another;
      if (typing && byId.hidden) page.focus();
      if (!focused(byId)) byId.value = known ? '' : step.page;
      as.set(step.as ?? 'dialog');
      fromRow.hidden = step.as !== 'panel';
      // The start of the line is offered only to a page that already has it.
      const sides: PanelSide[] = ['end', ...(step.side === 'start' ? (['start'] as const) : []), 'left', 'right', 'top', 'bottom'];
      fill(el, from, sides.map((side) => ({ value: side === 'end' ? '' : side, label: w.sides[side] })));
      if (!focused(from)) from.value = step.side && step.side !== 'end' ? step.side : '';
      fromHint.textContent = w.sideHints[step.side ?? 'end'] ?? '';
      fromHint.hidden = !fromHint.textContent;
      startsWith.update(step.values ?? {});
      into.update(step.into ?? {});
    });
  }

  // ---- a line added to a table ----
  function addLineSettings() {
    fieldPicker(w.table, 'field', (def) => def.type === 'one2many');
    const lines = () => {
      const def = current.do === 'addLine' ? ctx.page().fields[current.field] : undefined;
      return def?.type === 'one2many' ? Object.entries(def.fields).map(([name, f]) => ({ value: name, label: f.label || name })) : [];
    };
    const values = stepMap(el, {
      words,
      functions: ctx.functions,
      label: w.lineValues,
      addWords: w.addValue,
      nameLabel: w.field,
      names: lines,
      valueLabel: (name) => w.valueFrom(name),
      reads: () => ctx.page(),
      commit: (map, typing) => ctx.change({ values: map }, typing),
    });
    parts.push(values.element);
    updates.push((step) => step.do === 'addLine' && values.update(step.values ?? {}));
  }

  // ---- what is checked ----
  function checkSettings() {
    const which = segmented<'all' | 'some'>(el, w.check, [{ value: 'all', words: w.wholeForm }, { value: 'some', words: w.onlyFields }], (value) => {
      if (value === 'all') ctx.change({ fields: null });
      else if (value === 'some') {
        const first = fieldsOf()[0];
        if (first) ctx.change({ fields: [first.value] });
      }
    });
    const boxes = el('div', { class: 'fd-do-fields', role: 'group', 'aria-label': w.onlyFields });
    parts.push(row(el, w.check, which.element, boxes));
    updates.push((step) => {
      if (step.do !== 'check') return;
      which.set(step.fields?.length ? 'some' : 'all');
      boxes.hidden = !step.fields?.length;
      const all = fieldsOf();
      const key = JSON.stringify(all);
      if (boxes.dataset['fields'] !== key) {
        boxes.dataset['fields'] = key;
        boxes.replaceChildren(
          ...all.map((f) => {
            const tick = el('input', { type: 'checkbox', value: f.value }) as HTMLInputElement;
            tick.addEventListener('change', () => {
              const picked = [...boxes.querySelectorAll<HTMLInputElement>('input:checked')].map((t) => t.value);
              ctx.change({ fields: picked.length ? picked : null });
            });
            return el('label', { class: 'fd-q-required' }, tick, el('span', {}, f.label));
          })
        );
      }
      for (const tick of boxes.querySelectorAll<HTMLInputElement>('input')) tick.checked = !!step.fields?.includes(tick.value);
    });
  }

  // ---- only when ----
  function whenSetting() {
    const start = el('button', { type: 'button', class: 'fd-button fd-button-link fd-q-when fd-do-when-start' }, w.onlyWhen);
    let begun = false;
    const box = formulaBox(el, {
      words,
      label: w.runsOnlyWhen,
      placeholder: w.whenPlaceholder,
      check: (page, source) => formulaProblem(page, source, words, ctx.functions),
      commit: (source) => ctx.change({ when: source || null }, true),
    });
    const holder = row(el, w.runsOnlyWhen, box.element, box.problem);
    holder.classList.add('fd-do-when');
    start.addEventListener('click', () => {
      begun = true;
      holder.hidden = false;
      start.hidden = true;
      box.input.focus();
    });
    parts.push(holder, start);
    updates.push((step) => {
      const has = step.when !== undefined;
      holder.hidden = !has && !begun;
      start.hidden = !holder.hidden;
      box.update(ctx.page(), typeof step.when === 'string' ? step.when : '');
    });
  }

  const element = el('div', { class: 'fd-do-settings' }, ...parts);
  return {
    element,
    update(step) {
      current = step;
      for (const update of updates) update(step);
    },
    focusFirst() {
      // The first control on show: an open step's page list waits for the app's list of pages.
      const shown = (control: HTMLElement) => !control.closest('[hidden]');
      const controls = [...(first ? [first] : []), ...element.querySelectorAll<HTMLElement>('select, input, button')];
      controls.find(shown)?.focus();
    },
  };
}

/** A new step of a kind, as "Add a step" begins it: what can be picked first is; what must be written waits for it. */
export function newStep(page: Page, kind: ActionStep['do']): ActionStep {
  const first = (test: (def: Field) => boolean) => Object.entries(page.fields).find(([, def]) => test(def))?.[0] ?? '';
  switch (kind) {
    case 'open':
      return { do: 'open', page: '' } as OpenStep;
    case 'set':
      return { do: 'set', field: first((def) => SETTABLE.has(def.type)), value: '' };
    case 'clear':
      return { do: 'clear', field: first(() => true) };
    case 'addLine':
      return { do: 'addLine', field: first((def) => def.type === 'one2many') };
    case 'goTo':
      return { do: 'goTo', target: showTargets(page)[0]?.id ?? '' };
    case 'say':
      return { do: 'say', message: '' };
    case 'ask':
      return { do: 'ask', message: '' };
    case 'call':
      return { do: 'call', action: '' };
    case 'openUrl':
      return { do: 'openUrl', url: '' };
    case 'post':
      return { do: 'post', message: '' };
    default:
      return { do: kind } as ActionStep;
  }
}
